import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { AppCard, AppNoInfo, AppRemixIcon } from "@/layout/AppComponents";
import ReactDOMServer from "react-dom/server";
import Translator from "../../../layout/components/helpers/Translator";
import { axiosPath, path } from "../../../routing/helpers/router";
import * as currentPageAction from "../../../store/actions/currentPageAction";
import routes from "./moduleComsaEnvironmentalRoutes";
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import useIsMounted from "../../../layout/components/hooks/useIsMounted";

export default function ModuleComsaEnvironmentalHomeScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector((state) => state?.currentPage?.moduleData);
  const [allowSectionSelection, setAllowSectionSelection] = useState(false);

  const handleOnSelectSection = useCallback(
    async (selectedSection) => {
      await axios
        .post(axiosPath("api_r_user_module_comsaEnvironmental_section_edit"), {
          selectedSection,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        })
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            navigate(
              path({
                name: `${
                  routes.find((ro) => ro.type === r.data?.sectionName)
                    ?.routeName
                }_${r.data?.sheetName}`,
                params: {
                  moduleBridgeHex,
                  subModuleBridgeHex,
                  plantHex,
                },
              })
            );
          }
        });
    },
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  const getRedirectSheetName = useCallback((sectionName) => {
    switch (sectionName) {
      case "office":
        return "normal_environmental_aspects";
      case "construction":
      case "industrial":
        return "data_input";
      default:
        return "";
    }
  }, []);

  useEffect(() => {
    if (moduleData) {
      if (moduleData?.selectedSection?.name) {
        navigate(
          path({
            name: `${
              routes.find((ro) => ro.type === moduleData?.selectedSection?.name)
                ?.routeName
            }_${getRedirectSheetName(moduleData?.selectedSection?.name)}`,
            params: {
              moduleBridgeHex,
              subModuleBridgeHex,
              plantHex,
            },
          })
        );
      } else {
        setAllowSectionSelection(true);
      }
    }
  }, [moduleData]);

  if (!allowSectionSelection) return <AppNoInfo />;

  return (
    <div className="p-3">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspects"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <h1 className="mb-4">
        <Translator
          file="module-comsaEnvironmental"
          name="environmental.aspects"
        />
      </h1>
      <SectionButtonContainer>
        {routes.map(
          (r) =>
            Object.keys(moduleData.sections).includes(r.type) && (
              <div key={r.type}>
                <AppCard
                  className="card mb-4"
                  bodyClassName="p-0"
                  onClick={() => handleOnSelectSection(r.type)}
                >
                  <SectionButton
                    className="card-body text-center pointer text-white"
                    bgColor={r.color}
                  >
                    <AppRemixIcon icon={r.icon} size={30} />
                    <div className="p-2">
                      <h5 className="text-white">
                        <Translator
                          file="module-comsaEnvironmental"
                          name={r.title}
                        />
                      </h5>
                    </div>
                  </SectionButton>
                </AppCard>
              </div>
            )
        )}
      </SectionButtonContainer>
    </div>
  );
}

const SectionButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  div {
    flex: 1 1;
  }
`;

const SectionButton = styled.div`
  background-color: ${(props) => props.bgColor};
`;
